import type {Variant} from 'motion';

type PopoverAnimation = {
    enter: Variant;
    exit: Variant;
    initial: Variant;
};

/**
 * Defines animation variants for entering and exiting animations on a popover component using Framer Motion.
 * The `PopoverEnterAnimation` constant is an object that specifies two states, `enter` and `exit`,
 * which are used to animate the component when it is entering or exiting the viewport, respectively.
 * The `enter` variant sets the component to full opacity (`1`) and moves it to its final position (`y: '0px'`),
 * while the `exit` variant sets the component to zero opacity (`0`) and moves it off-screen (`y: '80px'`).
 */
export const PopoverTransition: PopoverAnimation = {
    /**
     * The `enter` function generates a variant for the entering animation, accepting an `alignment` parameter to
     * customize the animation based on the popover's alignment. For center-aligned popovers, it adjusts the X position
     * to `-50%` to ensure it is properly centered. This variant fades the popover in to full opacity (`1`) and moves
     * it to its final position along the Y-axis (`y: '0'`), creating an engaging appearance transition.
     *
     * @param alignment The alignment of the popover content.
     * @returns The variant for the entering animation.
     */
    enter(alignment) {
        return {
            opacity: 1,
            transitionEnd: {pointerEvents: 'auto'},
            y: '5px',
            ...(alignment === 'center' ? {x: '-50%'} : {})
        };
    },
    /**
     * The `exit` function generates a variant for the exiting animation, accepting an `alignment` parameter to customize
     * the animation based on the popover's alignment. For center-aligned popovers, it adjusts the X position to `-50%` to
     * ensure it is properly centered. This variant fades the popover out to zero opacity (`0`) and moves it off-screen
     * along the Y-axis (`y: '80px'`), creating a smooth exit transition.
     *
     * @param alignment The alignment of the popover content.
     * @returns The variant for the exiting animation.
     */
    exit(alignment) {
        return {
            opacity: 0,
            pointerEvents: 'none',
            y: '15px',
            ...(alignment === 'center' ? {x: '-50%'} : {})
        };
    },
    /**
     * The `initial` function generates a variant for the initial state of the popover, setting the opacity to zero (`0`)
     * and moving it off-screen along the Y-axis (`y: '80px'`). For center-aligned popovers, it also adjusts the X
     * position to `-50%` to ensure the popover is centered properly when it first appears.
     * This variant is used to define the initial state of the popover before any animations are applied.
     *
     * @param alignment The alignment of the popover content.
     *
     * @returns The variant for the initial state of the popover.
     */
    initial(alignment) {
        return {
            opacity: 0,
            pointerEvents: 'none',
            y: '15px',
            ...(alignment === 'center' ? {x: '-50%'} : {})
        };
    }
};